import * as React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertTriangle, RefreshCw, Home, Bug, Copy } from 'lucide-react';
import { monitoring } from '@/lib/monitoring';
// Analytics import removed to avoid circular dependency
import { logger } from '@/lib/logger';
import { errorHandler } from '@/lib/errorHandler';

interface Props {
  children: React.ReactNode;
  fallback?: (error: Error, reset: () => void) => React.ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  showDetails?: boolean;
  resetKeys?: Array<string | number>;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  errorId: string | null;
  errorCount: number;
}

/**
 * Enhanced Error Boundary with Sentry integration
 * Catches JavaScript errors anywhere in the component tree
 */
export class ErrorBoundary extends React.Component<Props, State> {
  private resetTimeoutId: NodeJS.Timeout | null = null;
  private resetKeys: Array<string | number> = [];

  constructor(props: Props) {
    super(props);

    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      errorCount: 0,
    };

    this.resetKeys = props.resetKeys || [];
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
      errorId: `error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const { onError } = this.props;
    const { errorId, errorCount } = this.state;

    // Use existing error handler
    errorHandler.handle(error, {
      action: 'component_error',
      metadata: {
        errorInfo,
        errorId,
        errorCount: errorCount + 1,
      },
    });

    // Track error in analytics
    // Deferred: analytics.trackError would be called here
    logger.error('Error tracked in boundary', {
      feature: 'error-boundary',
      action: 'component-error',
      metadata: {
        componentStack: errorInfo.componentStack,
        errorId,
        errorCount: errorCount + 1,
      },
    });

    // Report to Sentry with enhanced context
    monitoring.reportError(error, {
      level: 'error',
      extra: {
        componentStack: errorInfo.componentStack,
        errorBoundary: true,
        errorId,
        props: this.getPropsForLogging(),
      },
      tags: {
        error_boundary: 'true',
        component_error: 'true',
      },
      fingerprint: ['error-boundary', error.name, error.message],
    });

    // Update error count
    this.setState(prevState => ({
      errorInfo,
      errorCount: prevState.errorCount + 1,
    }));

    // Call custom error handler if provided
    if (onError) {
      onError(error, errorInfo);
    }

    // Auto-retry after 10 seconds if it's the first error
    if (errorCount === 0) {
      this.scheduleReset(10000);
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { resetKeys } = this.props;
    const { hasError } = this.state;

    // Check if reset keys have changed
    if (hasError && resetKeys && prevProps.resetKeys) {
      const hasResetKeyChanged = resetKeys.some(
        (key, index) => key !== prevProps.resetKeys![index],
      );

      if (hasResetKeyChanged) {
        this.reset();
      }
    }
  }

  componentWillUnmount() {
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId);
    }
  }

  private scheduleReset = (delay: number) => {
    this.resetTimeoutId = setTimeout(() => {
      this.reset();
    }, delay);
  };

  reset = () => {
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId);
      this.resetTimeoutId = null;
    }

    // Track recovery
    // Deferred: analytics.track would be called here
    logger.info('Error boundary reset', {
      action: 'error_boundary_reset',
      metadata: {
        errorId: this.state.errorId,
        errorCount: this.state.errorCount,
      },
    });

    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    });
  };

  private getPropsForLogging = () => {
    const { children, fallback, onError, ...safeProps } = this.props;
    return safeProps;
  };

  private copyErrorDetails = () => {
    const { error, errorInfo, errorId } = this.state;

    const details = `
Error ID: ${errorId}
Error: ${error?.toString()}
Stack: ${error?.stack}
Component Stack: ${errorInfo?.componentStack}
Time: ${new Date().toISOString()}
URL: ${window.location.href}
User Agent: ${navigator.userAgent}
`;

    navigator.clipboard.writeText(details).then(() => {
      logger.log('Error details copied to clipboard');
    });
  };

  private handleReportBug = () => {
    const { errorId } = this.state;
    const bugReportUrl = `https://github.com/your-org/your-repo/issues/new?title=Error%20${errorId}&body=Error%20ID:%20${errorId}%0A%0APlease%20describe%20what%20you%20were%20doing%20when%20this%20error%20occurred.`;
    window.open(bugReportUrl, '_blank');
  };

  render() {
    const { children, fallback, showDetails = import.meta.env.DEV } = this.props;
    const { hasError, error, errorInfo, errorId, errorCount } = this.state;

    if (hasError && error) {
      // Custom fallback UI if provided
      if (fallback) {
        return <>{fallback(error, this.reset)}</>;
      }

      // Default error UI
      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-background">
          <div className="max-w-2xl w-full space-y-4">
            <Alert className="border-red-200 bg-red-50">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              <AlertTitle className="text-red-900">Something went wrong</AlertTitle>
              <AlertDescription className="text-red-700">
                An unexpected error has occurred. The error has been logged and our team has been
                notified.
                {errorCount > 1 && (
                  <span className="block mt-2 text-sm">
                    This error has occurred {errorCount} times in this session.
                  </span>
                )}
              </AlertDescription>
            </Alert>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Error Details</span>
                  {errorId && (
                    <span className="text-sm font-mono text-muted-foreground">#{errorId}</span>
                  )}
                </CardTitle>
                <CardDescription>
                  We apologize for the inconvenience. You can try refreshing the page or returning
                  to the home page.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-3 rounded-lg bg-muted">
                  <p className="text-sm font-medium text-red-600">
                    {error.name}: {error.message}
                  </p>
                </div>

                {showDetails && error.stack && (
                  <details className="space-y-2">
                    <summary className="cursor-pointer text-sm font-medium">
                      Technical Details (Development Mode)
                    </summary>
                    <div className="space-y-2">
                      <pre className="p-3 rounded-lg bg-muted text-xs overflow-x-auto">
                        {error.stack}
                      </pre>
                      {errorInfo?.componentStack && (
                        <div>
                          <p className="text-sm font-medium mb-1">Component Stack:</p>
                          <pre className="p-3 rounded-lg bg-muted text-xs overflow-x-auto">
                            {errorInfo.componentStack}
                          </pre>
                        </div>
                      )}
                    </div>
                  </details>
                )}

                <div className="flex flex-col sm:flex-row gap-2">
                  <Button onClick={this.reset} className="flex items-center gap-2">
                    <RefreshCw className="h-4 w-4" />
                    Try Again
                  </Button>

                  <Button
                    variant="outline"
                    onClick={() => (window.location.href = '/')}
                    className="flex items-center gap-2"
                  >
                    <Home className="h-4 w-4" />
                    Go to Home
                  </Button>

                  {showDetails && (
                    <>
                      <Button
                        variant="outline"
                        onClick={this.copyErrorDetails}
                        className="flex items-center gap-2"
                      >
                        <Copy className="h-4 w-4" />
                        Copy Details
                      </Button>

                      <Button
                        variant="outline"
                        onClick={this.handleReportBug}
                        className="flex items-center gap-2"
                      >
                        <Bug className="h-4 w-4" />
                        Report Bug
                      </Button>
                    </>
                  )}
                </div>

                {errorCount === 0 && (
                  <p className="text-xs text-muted-foreground">
                    This page will automatically try to recover in a few seconds...
                  </p>
                )}
              </CardContent>
            </Card>

            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="pt-6">
                <p className="text-sm text-blue-900">
                  <strong>Need help?</strong> If this problem persists, please contact support with
                  error ID{' '}
                  <code className="font-mono bg-blue-100 px-1 py-0.5 rounded">{errorId}</code>
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      );
    }

    return children;
  }
}

// Wrap specific parts of the app
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  fallback?: (error: Error, reset: () => void) => React.ReactNode,
) {
  return (props: P) => (
    <ErrorBoundary fallback={fallback}>
      <Component {...props} />
    </ErrorBoundary>
  );
}

/**
 * Hook to reset error boundary programmatically
 */
export function useErrorHandler() {
  return (error: Error, errorInfo?: React.ErrorInfo) => {
    logger.error('Error caught by useErrorHandler:', error);

    // Deferred: analytics.trackError would be called here
    logger.error('Error tracked in boundary', {
      feature: 'error-handler',
      action: 'manual-error',
      metadata: errorInfo,
    });

    monitoring.reportError(error, {
      level: 'error',
      extra: errorInfo,
    });

    throw error;
  };
}

/**
 * Async error boundary for handling Promise rejections
 */
export function AsyncErrorBoundary({ children }: { children: React.ReactNode }) {
  React.useEffect(() => {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      logger.error('Unhandled promise rejection:', event.reason);

      // Deferred: analytics.trackError would be called here
      logger.error('Unhandled rejection tracked', {
        feature: 'async-error-boundary',
        action: 'unhandled-rejection',
      });

      monitoring.reportError(new Error(event.reason), {
        level: 'warning',
        tags: {
          unhandled_rejection: 'true',
        },
      });
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  return <>{children}</>;
}

/**
 * Route-scoped error boundary (VF-37): contains a render crash to the current
 * route instead of replacing the whole app with the global error screen.
 * Errors still reach Sentry via the underlying ErrorBoundary.
 */
export function RouteErrorBoundary({ children }: { children: React.ReactNode }) {
  return (
    <ErrorBoundary
      fallback={(_error, reset) => (
        <div className="min-h-screen flex items-center justify-center p-6 bg-background">
          <div className="text-center space-y-4 max-w-md">
            <AlertTriangle className="h-10 w-10 text-destructive mx-auto" />
            <h2 className="text-xl font-semibold">Something went wrong on this page</h2>
            <p className="text-muted-foreground">
              The error has been logged. You can retry this page or go back to your dashboard.
            </p>
            <div className="flex items-center justify-center gap-3">
              <Button onClick={reset}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Try again
              </Button>
              <Button variant="outline" onClick={() => (window.location.href = '/dashboard')}>
                <Home className="mr-2 h-4 w-4" />
                Go to Dashboard
              </Button>
            </div>
          </div>
        </div>
      )}
    >
      {children}
    </ErrorBoundary>
  );
}
